import { Link } from 'react-router-dom';
import LegalShell from '../components/LegalShell';

const Terms = () => (
  <LegalShell title="Terms of" accent="Service">

      <section className="legal-section">
        <h2 className="legal-h2">1. Parties, scope and precedence</h2>
        <p className="legal-p">
          1.1 These Terms of Service ("Terms") govern every use of the Qombra platform, the QBrain
          API and SDK, QAgent and related documentation (together the "Services") provided by
          Qombra B.V., Valckenierstraat 65, 1018 WV Amsterdam, the Netherlands ("Qombra"). The
          contracting party is the business on whose behalf an account is created ("Customer").
        </p>
        <p className="legal-p">
          1.2 The Services are offered to businesses, professionals and other organisations only.
          Natural persons acting for purposes outside their trade, business or profession
          (consumers within the meaning of Art. 6:230g of the Dutch Civil Code) may not use the
          Services. Qombra may ask for proof of business status at any time.
        </p>
        <p className="legal-p">
          1.3 The following documents form part of the contract. In case of conflict, the earlier
          listed document prevails: (a) an individual written agreement between Customer and
          Qombra; (b) these Terms; (c) the <Link className="legal-link" to="/dpa">Data Processing
          Agreement</Link>; (d) the <Link className="legal-link" to="/aup">Acceptable Use
          Policy</Link> ("AUP"); (e) the documentation. The{' '}
          <Link className="legal-link" to="/privacy">Privacy Policy</Link> describes how Qombra
          processes personal data and is not a contractual document.
        </p>
        <p className="legal-p">
          1.4 Customer's own general terms and conditions do not apply.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">2. Account and conclusion of the contract</h2>
        <p className="legal-p">
          2.1 The contract is concluded when Customer creates an account, when Qombra grants
          access to the Services, or when an individual agreement is signed, whichever is earliest.
        </p>
        <p className="legal-p">
          2.2 Customer provides accurate and complete registration details and keeps them up to
          date. The person who creates the account confirms that they are authorised to bind
          Customer.
        </p>
        <p className="legal-p">
          2.3 Accounts, passwords and API keys are confidential and may not be shared outside
          Customer's organisation. Customer is responsible for all activity under its credentials
          and informs Qombra without delay at{' '}
          <a className="legal-link" href="mailto:hey@qombra.com">hey@qombra.com</a> of any
          suspected unauthorised use.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. The Services</h2>
        <p className="legal-p">
          3.1 The Services provide predictive modelling, forecasting and AI-assisted data
          preparation on tabular data through a web application, an API and an SDK. Qombra
          develops the Services continuously and may change or discontinue features, provided the
          core functionality that Customer pays for is not materially reduced during a paid term.
        </p>
        <p className="legal-p">
          3.2 Qombra uses commercially reasonable efforts to keep the Services available and may
          interrupt them for maintenance, security or capacity reasons. Scheduled maintenance is
          announced where reasonably possible.
        </p>
        <p className="legal-p">
          3.3 Use of the Services is subject to usage limits (for example the number of analyses,
          messages, output tokens, requests per hour and active API keys). Qombra sets the limits
          of the free tier and may adjust them; limits for paid use are set in the individual
          agreement. Customer may not circumvent limits, for example by creating multiple accounts.
        </p>
        <p className="legal-p">
          3.4 The free tier is provided "as is" and without any availability or performance
          commitment.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">4. Fees and payment</h2>
        <p className="legal-p">
          4.1 The free tier is free of charge. Paid use requires an individual written agreement
          that sets out the price model (a fixed price or usage-based fees), the usage limits and
          the term.
        </p>
        <p className="legal-p">
          4.2 Qombra invoices Customer directly. Unless the individual agreement provides
          otherwise, invoices are payable by bank transfer within 30 days of the invoice date. All
          prices exclude VAT and other applicable taxes.
        </p>
        <p className="legal-p">
          4.3 If Customer fails to pay on time, Qombra may charge statutory commercial interest
          (Art. 6:119a of the Dutch Civil Code) and reasonable collection costs, and may suspend
          the Services after a written reminder that sets a further payment period of at least 14
          days.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">5. Customer content</h2>
        <p className="legal-p">
          5.1 "Customer Content" means all data, tables, prompts, instructions and other material
          that Customer uploads to or makes available through the Services, including through
          the API, the SDK and connected data sources.
        </p>
        <p className="legal-p">
          5.2 Customer retains all rights in Customer Content. Customer grants Qombra a
          non-exclusive, worldwide, royalty-free licence to host, process and transmit Customer
          Content solely to provide, secure and support the Services for Customer. Qombra does not
          use Customer Content to train or improve models for other customers, and does not sell
          or share it.
        </p>
        <p className="legal-p">
          5.3 Customer warrants that it holds all rights and lawful bases required to make
          Customer Content available for processing by Qombra, and that Customer Content complies
          with the AUP.
        </p>
        <p className="legal-p">
          5.4 Where Customer Content contains personal data, Customer is the controller and Qombra
          the processor. The <Link className="legal-link" to="/dpa">Data Processing Agreement</Link>{' '}
          applies. Special categories of personal data (Art. 9 GDPR) and data relating to
          criminal convictions may be processed only where agreed in writing beforehand.
        </p>
        <p className="legal-p">
          5.5 Customer keeps its own copies of Customer Content. The Services are not a backup or
          archiving service.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">6. Output</h2>
        <p className="legal-p">
          6.1 "Output" means predictions, forecasts, explanations, prepared data, generated code
          and other results the Services return for Customer. Customer may use Output for its own
          business purposes. As between the parties, Customer owns the Output, with the exception
          of the models, weights and software of Qombra used to produce it.
        </p>
        <p className="legal-p">
          6.2 Output is produced by statistical and machine-learning methods and is
          probabilistic. It may be incomplete, inaccurate or unsuitable for Customer's purpose.
          Qombra does not warrant the accuracy, completeness or fitness of Output. Customer
          evaluates Output with its own expertise before relying on it and remains solely
          responsible for decisions and actions based on it.
        </p>
        <p className="legal-p">
          6.3 Where Output concerns natural persons, Customer ensures appropriate human review and
          complies with Art. 22 GDPR and other applicable law before taking any decision that
          produces legal or similarly significant effects on a person.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">7. Artificial intelligence</h2>
        <p className="legal-p">
          7.1 The Services are general-purpose prediction and data-preparation tools. They are not
          intended for, and may not be used for or as a component of, any practice prohibited by
          Art. 5 of Regulation (EU) 2024/1689 (the "AI Act") or any high-risk use listed in Annex
          III of the AI Act. The AUP lists these uses. Using the Services for such a purpose is a
          material breach of these Terms.
        </p>
        <p className="legal-p">
          7.2 QAgent is an AI system. Its messages are generated by a language model and are
          marked as such in the interface and the documentation. Customer informs its own users
          accordingly where it exposes QAgent output to them.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">8. Intellectual property</h2>
        <p className="legal-p">
          8.1 Qombra and its licensors retain all intellectual property rights in the Services,
          including the models, model weights, software, SDK, documentation and brand. No rights
          are granted other than the limited, non-exclusive, non-transferable right to use the
          Services during the term in accordance with these Terms.
        </p>
        <p className="legal-p">
          8.2 Customer shall not copy, modify, reverse engineer, decompile or extract the models,
          weights or source code of the Services, nor use the Services or their Output to train,
          distil or benchmark a competing model, except where mandatory law permits.
        </p>
        <p className="legal-p">
          8.3 Qombra may use suggestions and feedback from Customer without restriction or
          compensation.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">9. Confidentiality</h2>
        <p className="legal-p">
          Each party keeps the other party's non-public information confidential, uses it only to
          perform the contract and protects it with at least reasonable care. This does not apply
          to information that is or becomes public without breach, was already lawfully known, or
          must be disclosed by law or court order (in which case the disclosing party informs the
          other party where legally permitted). The obligation survives the contract for five
          years.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">10. Suspension</h2>
        <p className="legal-p">
          Qombra may suspend Customer's access, in whole or in part, where (a) Customer breaches
          the AUP or clause 7 or 8; (b) the account or its credentials are used without
          authorisation or create a security risk; (c) usage materially exceeds the agreed
          limits; (d) payment is more than 14 days overdue after a reminder; or (e) suspension is
          required by law or a competent authority. Qombra informs Customer of the suspension and
          its reason, in advance where reasonably possible, and lifts it once the cause is
          remedied.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">11. Term, termination and data export</h2>
        <p className="legal-p">
          11.1 Free use runs for an indefinite period. Either party may end it at any time;
          Customer does so by deleting its account.
        </p>
        <p className="legal-p">
          11.2 Paid use runs for the term stated in the individual agreement. Where the agreement
          is silent, the term is 12 months, renewed automatically for successive 12-month periods
          unless either party gives written notice at least 2 months before the end of the
          current period.
        </p>
        <p className="legal-p">
          11.3 Either party may terminate the contract with immediate effect by written notice
          where the other party materially breaches the contract and, where the breach can be
          remedied, fails to remedy it within 14 days after notice; or where the other party is
          declared bankrupt, is granted a suspension of payments or ceases its business.
        </p>
        <p className="legal-p">
          11.4 Customer can export Customer Content and Output in commonly used, machine-readable
          formats at any time during the term and for 30 days after it ends. Qombra does not
          charge for switching or export. Thereafter Qombra deletes Customer Content and Output
          within 30 days, except for copies in backups, which are overwritten within a further 35
          days, and data Qombra must retain by law.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">12. Warranties and liability</h2>
        <p className="legal-p">
          12.1 Except as expressly stated in these Terms or an individual agreement, Qombra
          provides the Services without warranties of any kind, including as to merchantability,
          fitness for a particular purpose, uninterrupted or error-free operation, or the
          accuracy of Output.
        </p>
        <p className="legal-p">
          12.2 For the free tier, Qombra is not liable for any damage except where it results from
          intent or deliberate recklessness (opzet of bewuste roekeloosheid) of Qombra or its
          managers.
        </p>
        <p className="legal-p">
          12.3 For paid use, Qombra's total liability under or in connection with the contract,
          per contract year and regardless of the legal basis, is limited to the fees paid by
          Customer for the Services in the 12 months preceding the event that gave rise to the
          claim.
        </p>
        <p className="legal-p">
          12.4 Qombra is not liable for indirect or consequential damage, including lost profit,
          lost revenue, lost savings, loss of goodwill, business interruption, loss or corruption
          of data, or damage resulting from decisions made on the basis of Output.
        </p>
        <p className="legal-p">
          12.5 The limitations in this clause do not apply where liability cannot be limited under
          mandatory law, including liability for intent or deliberate recklessness, death or
          personal injury.
        </p>
        <p className="legal-p">
          12.6 Any claim against Qombra lapses unless brought before a court within 12 months after
          Customer became aware, or should reasonably have become aware, of the damage.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">13. Indemnity</h2>
        <p className="legal-p">
          Customer indemnifies Qombra against all third-party claims, administrative fines and
          related costs arising from Customer Content, from Customer's use of the Services or
          Output in breach of these Terms, the AUP or applicable law, or from Customer's failure to
          hold the rights and lawful bases warranted in clause 5.3, to the extent the claim is
          attributable to Customer.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">14. Changes to these Terms</h2>
        <p className="legal-p">
          Qombra may amend these Terms, the AUP and the Data Processing Agreement for valid
          reasons, such as changes in the Services, in the law or in the providers Qombra uses.
          Qombra notifies Customer of material changes by email or in the platform at least 30
          days before they take effect. If Customer objects in writing before that date, Customer
          may terminate the contract with effect from the date the change takes effect; otherwise
          the change is deemed accepted. Changes required by law may take effect earlier.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">15. General</h2>
        <p className="legal-p">
          15.1 Dutch law applies. The United Nations Convention on Contracts for the
          International Sale of Goods does not apply.
        </p>
        <p className="legal-p">
          15.2 Disputes are submitted exclusively to the competent court in Amsterdam, the
          Netherlands, without prejudice to Qombra's right to bring a claim before the court of
          Customer's seat.
        </p>
        <p className="legal-p">
          15.3 If a provision of these Terms is invalid, the remaining provisions remain in force
          and the invalid provision is replaced by a valid one that comes closest to its purpose.
        </p>
        <p className="legal-p">
          15.4 Customer may not assign the contract without Qombra's written consent. Qombra may
          assign it to an affiliate or a successor of its business.
        </p>
        <p className="legal-p">
          15.5 Notices to Qombra are sent to{' '}
          <a className="legal-link" href="mailto:hey@qombra.com">hey@qombra.com</a>; notices to
          Customer to the email address of the account. These Terms are drawn up in English; the
          English text prevails over any translation.
        </p>
      </section>
  </LegalShell>
);

export default Terms;
